import { authErrorStorage, describeToken, tokenStorage } from './auth';

export const MOCK = !!import.meta.env.VITE_MOCK;

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
/** Se dispara en cualquier 401: la app borra el token y muestra el login. */
export function onUnauthorized(l: Listener) {
  unauthorizedListeners.add(l);
  return () => {
    unauthorizedListeners.delete(l);
  };
}

export async function apiRaw(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = tokenStorage.get();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  // En modo mock la API en memoria se carga bajo demanda (no entra en el bundle de producción)
  const res = MOCK
    ? await (await import('../mocks/mockApi')).mockFetch(path, { ...init, headers })
    : await fetch(path, { ...init, headers });
  if (!res.ok) {
    let code = 'error';
    let message = `Error ${res.status}`;
    let body: { error?: string; message?: string; reason?: string } = {};
    try {
      body = (await res.json()) as typeof body;
      code = body.error ?? code;
      message = body.message ?? body.error ?? message;
    } catch {
      /* cuerpo no JSON */
    }
    if (res.status === 401) {
      // Se guarda el motivo para enseñarlo en el login (si no, parece que «rebota» sin explicación)
      authErrorStorage.set({
        at: new Date().toISOString(),
        path,
        reason: body.reason ?? 'unknown',
        message,
        // Datos de la propia sesión (decodificados aquí; la API no los devuelve)
        claims: tokenStorage.get() ? describeToken(tokenStorage.get()!) : null,
      });
      console.warn('[auth] 401 en', path, '→', body.reason, message);
      tokenStorage.clear();
      unauthorizedListeners.forEach((l) => l());
    }
    throw new ApiError(res.status, code, message);
  }
  return res;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiRaw(path, init);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const send = <T>(method: string, path: string, body?: unknown) =>
  api<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });
